import { useCallback, useEffect, useRef, useState } from 'react'

import type { DateRange } from '../calendar'
import type { VuiComponent } from '../core'
import type { DatePickerProps } from './datePicker.types'

import Box from '../box'
import Button, { LineButton } from '../button'
import Calendar, { maxDateTimestamp, minDateTimestamp } from '../calendar'
import { omitThemingProps, useStyleConfig, vui } from '../core'
import Popover from '../popover'
import { cs } from '../utils'
import { DateInput } from './dateInput'
import { DatePickerTriggerButton } from './datePickerTriggerButton'

/** Select or input a date. */
export const DatePicker = vui<'span', DatePickerProps>((props, ref) => {
  const {
    autoOpened,
    boundaries,
    popupPosition,
    placement,
    selectedDate: selectedDateProps,
    showClearButton,
    showDateInput = true,
    slotBottom,
    slotTop,
    onSelected,
    children,
    className,
    ...rest
  } = omitThemingProps(props)
  const styles = useStyleConfig('DatePicker', props)

  const [selectedDate, setSelectedDate] = useState<Date | undefined>(selectedDateProps)
  const [isOpen, setIsOpen] = useState(!selectedDateProps && !!autoOpened)
  const [isInvalidInputDate, setIsInvalidInputDate] = useState(showDateInput)
  const [isInvalidSelectedDate, setIsInvalidSelectedDate] = useState(!selectedDate)
  const [isClearButtonVisible, setIsClearButtonVisible] = useState(!!(showClearButton && selectedDateProps))
  const initialDateRef = useRef<Date | undefined>(selectedDateProps)

  const isStartDateInRange = (date: Date) =>
    !!boundaries?.startDate && !!date ? date.getTime() - boundaries.startDate.getTime() >= 0 : true

  const isEndDateInRange = (date: Date) =>
    !!boundaries?.endDate && !!date ? date.getTime() - boundaries.endDate.getTime() <= 0 : true

  const isValidDateRange = (date: Date) => {
    const minDateTime = new Date(minDateTimestamp)
    const maxDateTime = new Date(maxDateTimestamp)
    return date >= minDateTime && date <= maxDateTime
  }

  useEffect(() => {
    if (!isOpen) {
      setSelectedDate(selectedDateProps)
      setIsClearButtonVisible(!!(showClearButton && selectedDateProps))
      initialDateRef.current = selectedDateProps
    }
  }, [isOpen, selectedDateProps, showClearButton])

  useEffect(() => {
    setIsInvalidSelectedDate(
      (selectedDate && !isStartDateInRange(selectedDate)) ||
        (selectedDate && !isEndDateInRange(selectedDate)) ||
        (selectedDate && !isValidDateRange(selectedDate)) ||
        !selectedDate,
    )
  }, [selectedDate])

  const onApplyDates = useCallback(
    (date?: Date, close?: () => void) => {
      onSelected?.(date)
      initialDateRef.current = date
      setIsClearButtonVisible(!!(showClearButton && date))
      close?.()
    },
    [onSelected, showClearButton],
  )

  const onClear = useCallback(
    (close?: () => void) => {
      setSelectedDate(undefined)
      onSelected?.(undefined)
      close?.()
    },
    [onSelected],
  )

  const handlePopoverOpen = useCallback(() => setIsOpen(true), [])
  const handlePopoverClose = useCallback(() => setIsOpen(false), [])

  const selectDate = (range: DateRange) => setSelectedDate(range?.startDate)

  const onDateInputValidate = useCallback(
    (date?: Date): string => {
      const minDateTime = new Date(minDateTimestamp)
      const maxDateTime = new Date(maxDateTimestamp)

      if (date) {
        const conditions: [boolean, string][] = [
          [isNaN(date.getTime()), 'Supported format is YYYY-MM-DD'],
          [date < minDateTime, 'Invalid date, the minimum date time is 1753-01-02'],
          [date > maxDateTime, 'Invalid date, the maximum date time is 9000-12-31'],
          [!!boundaries?.startDate && date.getTime() - boundaries.startDate.getTime() < 0, 'Invalid date'],
          [!!boundaries?.endDate && date.getTime() - boundaries.endDate.getTime() > 0, 'Invalid date'],
        ]
        const [validateErrorPair] = conditions.filter(([result]) => result)

        if (validateErrorPair) {
          const [_, message] = validateErrorPair
          setIsInvalidInputDate(true)
          return message
        }
      }
      return ''
    },
    [boundaries],
  )

  const onDateInputChange = useCallback((date?: Date) => setSelectedDate(date), [])
  const onDateInputValidateStatus = useCallback((inValid: boolean) => setIsInvalidInputDate(!inValid), [])

  return (
    <Box className={cs('vui-datePicker', className)} position="relative" ref={ref} {...styles} {...rest}>
      <Popover
        defaultIsOpen={!selectedDateProps && autoOpened}
        onClose={handlePopoverClose}
        onOpen={handlePopoverOpen}
        placement={placement ? placement : popupPosition === 'right' ? 'bottom-end' : undefined}
      >
        {({ close }) => (
          <>
            <Popover.Trigger as={Box}>{children}</Popover.Trigger>
            <Popover.Content column>
              {!!slotTop && <Box px={2}>{slotTop}</Box>}
              {showDateInput && (
                <Box display="block" mt={2} mx={2}>
                  <DateInput
                    autoFocus
                    initialDate={selectedDate}
                    onChange={onDateInputChange}
                    onValidate={onDateInputValidate}
                    setValidateStatus={onDateInputValidateStatus}
                  />
                </Box>
              )}
              <Box mx={2} my={1}>
                <Calendar
                  boundaries={boundaries ?? { startDate: undefined, endDate: undefined }}
                  fixedNumberOfWeeks
                  fontWeight={500}
                  minH={200}
                  mode="exact"
                  onSelectDates={range => selectDate(range)}
                  selectedDates={{ startDate: selectedDate, endDate: undefined }}
                  w="100%"
                />
              </Box>
              <Box alignItems="center" flex={1} ml="auto" pb={2} px={2}>
                {isClearButtonVisible && (
                  <LineButton flex={1} onClick={() => onClear(close)} size="md">
                    Clear
                  </LineButton>
                )}
                <Button
                  disabled={isInvalidSelectedDate || isInvalidInputDate}
                  flex={1}
                  ml={isClearButtonVisible ? 1 : 0}
                  onClick={() => onApplyDates(selectedDate, close)}
                  size="md"
                >
                  Apply
                </Button>
              </Box>
              {!!slotBottom && <Box px={2}>{slotBottom}</Box>}
            </Popover.Content>
          </>
        )}
      </Popover>
    </Box>
  )
}) as VuiComponent<'span', DatePickerProps> & {
  TriggerButton: typeof DatePickerTriggerButton
}

DatePicker.TriggerButton = DatePickerTriggerButton
DatePicker.displayName = 'DatePicker'

export default DatePicker
